/**
 * Lightweight, fire-and-forget analytics tracker.
 *
 * Usage:
 *   import { track } from './analytics';
 *   track('question_open', { question_id: 'q_001', category: 'Caching' });
 *
 * Events flush automatically every 15 s or when 10 events accumulate.
 * A beforeunload listener fires a synchronous page_exit event via sendBeacon.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BACKEND_URL}/api/analytics/event`;
const FLUSH_INTERVAL_MS = 15_000;
const FLUSH_BATCH_SIZE = 10;

// One UUID per browser tab session
function getBrowserSessionId() {
  let id = sessionStorage.getItem('_bs_id');
  if (!id) {
    id = 'bs_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('_bs_id', id);
  }
  return id;
}

let queue = [];
let flushTimer = null;

function getToken() {
  return localStorage.getItem('session_token') || null;
}

function buildEvent(eventName, properties = {}) {
  return {
    event: eventName,
    properties,
    browser_session_id: getBrowserSessionId(),
    timestamp: new Date().toISOString(),
  };
}

async function flush() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length); // drain
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ events: batch }),
    });
  } catch (_) {
    // silently swallow — analytics should never break the app
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

function ga4Event(eventName, properties = {}) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, properties);
  }
}

/** Track an event. Always fire-and-forget — never await this. */
export function track(eventName, properties = {}) {
  queue.push(buildEvent(eventName, properties));
  if (queue.length >= FLUSH_BATCH_SIZE) {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    flush();
  } else {
    scheduleFlush();
  }
  ga4Event(eventName, properties);
}

/**
 * Track a share action as a `share_clicked` event.
 *   channel: 'linkedin' | 'x' | 'copy' | 'download' | …
 *   surface: where the share UI lives, e.g. 'feedback' or 'feedback_personal_best'
 */
export function trackShare(channel, surface, props = {}) {
  track('share_clicked', { channel, surface, ...props });
}

// ── Page exit tracking ──────────────────────────────────────────────────────

let _pageEntryTime = Date.now();
let _currentPage = window.location.pathname;

export function trackPageView(page) {
  // Fire exit for the previous page
  const timeSpentMs = Date.now() - _pageEntryTime;
  if (_currentPage && _currentPage !== page) {
    // Use sendBeacon so exit events survive navigation / tab close.
    // sendBeacon can't set headers, so the token travels in the body (never the URL).
    const exitPayload = {
      events: [buildEvent('page_exit', { page: _currentPage, time_spent_ms: timeSpentMs })],
      token: getToken() || undefined,
    };
    const blob = new Blob([JSON.stringify(exitPayload)], { type: 'application/json' });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, blob);
    }
  }

  _currentPage = page;
  _pageEntryTime = Date.now();
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', { page_path: page });
  }
  track('page_view', { page });
}

// Flush remaining events on tab close
window.addEventListener('beforeunload', () => {
  const timeSpentMs = Date.now() - _pageEntryTime;
  const exitPayload = {
    events: [
      ...queue,
      buildEvent('page_exit', { page: _currentPage, time_spent_ms: timeSpentMs }),
    ],
    token: getToken() || undefined,
  };
  queue = [];
  const blob = new Blob([JSON.stringify(exitPayload)], { type: 'application/json' });
  if (navigator.sendBeacon) {
    navigator.sendBeacon(ENDPOINT, blob);
  }
});
